// His bag as the site passes it on and says it (app/api/bag/route.js, components/ui/BagPanel.jsx).
//
// The town's view (sim-server world/Bag.js bagView) is read through to every visitor with the pop-up open, so it is
// cut here to what the pop-up draws: no positions, nobody named but by username (Sam, 2 Nov: "in public, we always
// refer to users by their username, never by their actual name"), and a spec only where a picture needs it. Anything
// the town adds later stays off the page until it is listed here. Pure, so the test reads it without Next.

import { BAG_SLOTS } from './bag.js'
import { DRAWN } from '../items/parts.js'

const HOW = ['found', 'gift', 'vending', 'bought']
const text = (v, max) => String(v ?? '').trim().slice(0, max)

function entry(e) {
  if (!e || typeof e !== 'object') return null
  const kind = e.kind === 'trinket' || e.kind === 'item' ? e.kind : null
  if (!kind || !e.id) return null
  const out = { id: text(e.id, 80), kind, name: text(e.name, 80) }
  if (kind === 'item') {
    out.itemId = text(e.itemId, 40)
    // a meal's build is what its picture is made of; a disk's number picks its colour; nothing else is drawn from
    if (DRAWN.includes(out.itemId) && e.spec && typeof e.spec === 'object') out.spec = e.spec
    // (the town keeps a disk's number as it is printed on the label, '07')
    else if (/^\d{1,3}$/.test(String(e.spec?.disk ?? ''))) out.spec = { disk: String(e.spec.disk) }
  } else {
    out.trinketKind = text(e.trinketKind, 40)
    if (e.colour) out.colour = text(e.colour, 16)
  }
  if (e.note) out.note = text(e.note, 240)
  out.from = { who: e.from?.who ? text(e.from.who, 40) : null, how: HOW.includes(e.from?.how) ? e.from.how : null }
  if (Number.isFinite(e.at)) out.at = e.at
  if (Number.isInteger(e.place)) out.place = e.place
  if (e.ago) out.ago = text(e.ago, 40)
  return out
}

const list = (v, max) => (Array.isArray(v) ? v.map(entry).filter(Boolean).slice(0, max) : [])

// his shelves, one at each roost that has one (Sam, 3 Nov: "perhaps we give multiple shelves, since he has multiple
// roosts now"): each by the name a visitor knows it by, with what is on it, its place the slot it is in. only the ones
// with something on, and never where they are: the name says that
function shelves(v) {
  if (!Array.isArray(v)) return []
  return v.slice(0, 8)
    .map((sh) => ({ nest: text(sh?.nest, 40), name: text(sh?.name, 80) || 'His shelf', things: list(sh?.things, 12) }))
    .filter((sh) => sh.things.length)
}

/**
 * The town's bag view, cut to what the pop-up shows. Always nine places, empty ones null, each thing's place its index,
 * and `newest` the id of the one it opens on; his shelves with something on them, and what is at home on none. A town
 * from the morning of 3 Nov still sends his beak apart and eight places: the beak is folded in first, as the town folds
 * it into the first place on its own file, and what was in it, which that town showed over his head, is the newest.
 * With no word from the town, the last thing in the bag is.
 */
export function cleanBagView(raw) {
  const old = raw && typeof raw === 'object' && 'beak' in raw
  const from = old ? [raw.beak, ...(Array.isArray(raw.bag) ? raw.bag : [])] : (Array.isArray(raw?.bag) ? raw.bag : [])
  const bag = Array.from({ length: BAG_SLOTS }, (_, i) => {
    const e = entry(from[i])
    if (e) e.place = i
    return e
  })
  const ids = bag.filter(Boolean).map((e) => e.id)
  const named = [text(raw?.newest, 80), old ? bag[0]?.id : null].find((id) => id && ids.includes(id))
  const newest = named || ids[ids.length - 1] || null
  return {
    slots: BAG_SLOTS,
    bag,
    newest,
    shelves: shelves(raw?.shelves),
    home: list(raw?.home, 60),
    ground: list(raw?.ground, 12),
    giftsToday: Number.isFinite(raw?.giftsToday) ? raw.giftsToday : 0,
  }
}

/**
 * Where and when he got it, the way somebody would say it: "found in the junk heap yesterday", "from tilly, 2 hours
 * ago", "out of the vending machine". The when is the town's own words (`ago`).
 */
export function gotLine(e) {
  const who = e?.from?.who ? text(e.from.who, 40) : null
  const ago = text(e?.ago, 40)
  switch (e?.from?.how) {
    // what he finds is found at the junk heap (sim-server world/Hoard.js), and the when goes straight on
    case 'found': return ago ? `found in the junk heap ${ago}` : 'found in the junk heap'
    case 'vending': return [who ? `from ${who}, out of the vending machine` : 'out of the vending machine', ago].filter(Boolean).join(', ')
    case 'gift': return [who ? `from ${who}` : 'from somebody', ago].filter(Boolean).join(', ')
    // (with his own money, from the prints he sells: the town says bought only of what he bought himself and names
    // nobody; something a visitor bought him out of the machine comes as vending, with their username)
    case 'bought': return ['he bought it himself', ago].filter(Boolean).join(', ')
    default: return [who ? `from ${who}` : '', ago].filter(Boolean).join(', ')
  }
}

/** When a gift opens again for somebody who has given one today: the town counts the day by UTC (GIFTS_PER_DAY). */
export function nextGiftAt(now = Date.now()) {
  const d = new Date(now)
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1)
}
